import React from 'react';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import { LoadingIndicator, Notice } from '../components/status';
import type {
  CapaRegisterDetailResponse,
  CapaRegisterFilters,
  CapaRegisterListResponse,
  CapaRegisterOverview,
  CapaRegisterRow
} from '../contracts/capaSummaryContracts';
import { usePortalContext } from '../context/PortalContext';
import { CONCEALED_REPORT_NAME, loadReportNameConcealment } from '../services/reportNameConcealmentClient';

type CapaTenantOption = AdminTenantOption & {
  accessibleInstanceCount?: number;
};

type CapaTenantPayload = {
  tenantId: string;
  displayName: string;
  sharePointHost?: string;
  accessibleInstanceCount?: number;
};

type CapaTenantsResponse = {
  tenants?: CapaTenantPayload[];
  defaultTenantId?: string;
};

type ReviewerAccessAssignment = {
  accessId: number;
  tenantId: string;
  userId: string;
  instanceId: string;
  grantedBy?: string;
  grantedAt: string;
  siteUrl?: string;
  pageUrl?: string;
  lastSeenAt?: string;
};

type ReviewerTenantInstance = {
  instanceId: string;
  siteUrl?: string;
  pageUrl?: string;
  lastSeenAt?: string;
};

type ReviewerAccessResponse = {
  tenantId: string;
  assignments?: ReviewerAccessAssignment[];
  instances?: ReviewerTenantInstance[];
};

const defaultFilters: CapaRegisterFilters = {
  overdueOnly: false,
  effectivenessReviewOnly: false,
  closedAuditOnly: false,
  top: 100
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 16,
  background: '#fff'
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 16
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeCsv = (value: string | number | undefined): string => {
  const text = String(value ?? '');
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const buildSourceUrl = (row: CapaRegisterRow | undefined): string | undefined => {
  if (!row?.pageUrl && !row?.siteUrl) {
    return undefined;
  }
  const base = row.pageUrl
    ? new URL(row.pageUrl, row.siteUrl || window.location.origin)
    : new URL(row.siteUrl!);
  base.searchParams.set('auditId', String(row.auditId));
  base.searchParams.set('findingId', String(row.parentFindingId));
  base.searchParams.set('capaId', String(row.capaId));
  return base.toString();
};

const mapTenantOption = (tenant: CapaTenantPayload): CapaTenantOption => ({
  tenantId: tenant.tenantId,
  displayName: tenant.displayName,
  sharePointHost: tenant.sharePointHost,
  isBillingTenant: false,
  isSessionTenant: false,
  accessibleInstanceCount: tenant.accessibleInstanceCount
});

const formatDate = (value?: string): string => value ? new Date(value).toLocaleDateString() : '—';
const formatScore = (value?: number): string => typeof value === 'number' ? value.toFixed(2) : '—';

const CapaRegister = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const canManageClients = tenant?.capabilities?.canManageCapaAssignments === true;
  const canViewRegister = tenant?.capabilities?.canUseCapa === true;
  const [deployableTenants, setDeployableTenants] = React.useState<CapaTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = React.useState('');
  const [filters, setFilters] = React.useState<CapaRegisterFilters>(defaultFilters);
  const [overview, setOverview] = React.useState<CapaRegisterOverview | null>(null);
  const [rows, setRows] = React.useState<CapaRegisterListResponse | null>(null);
  const [detail, setDetail] = React.useState<CapaRegisterDetailResponse | null>(null);
  const [selectedKey, setSelectedKey] = React.useState<string>('');
  const [assignments, setAssignments] = React.useState<ReviewerAccessAssignment[]>([]);
  const [tenantInstances, setTenantInstances] = React.useState<ReviewerTenantInstance[]>([]);
  const [reviewerEmail, setReviewerEmail] = React.useState('');
  const [assignmentInstanceId, setAssignmentInstanceId] = React.useState('');
  const [loadingOverview, setLoadingOverview] = React.useState(false);
  const [loadingRows, setLoadingRows] = React.useState(false);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [loadingAssignments, setLoadingAssignments] = React.useState(false);
  const [savingAssignment, setSavingAssignment] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [concealNamesInReports, setConcealNamesInReports] = React.useState(false);
  const [privacyReady, setPrivacyReady] = React.useState(false);

  React.useEffect(() => {
    if (!selectedTenantId) {
      setPrivacyReady(false);
      return;
    }
    let cancelled = false;
    setPrivacyReady(false);
    loadReportNameConcealment(selectedTenantId)
      .then(conceal => {
        if (!cancelled) {
          setConcealNamesInReports(conceal);
          setPrivacyReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) setPrivacyReady(false);
      });
    return () => { cancelled = true; };
  }, [selectedTenantId]);

  const selectedTenant = React.useMemo(
    () => deployableTenants.find((entry) => entry.tenantId === selectedTenantId) ?? null,
    [deployableTenants, selectedTenantId]
  );

  const loadTenants = React.useCallback(async (): Promise<void> => {
    try {
      const response = await fetch('/api/admin/capa/tenants', { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Tenants ${response.status}`);
      }
      const payload = (await response.json()) as CapaTenantsResponse;
      const tenants = Array.isArray(payload.tenants) ? payload.tenants.map(mapTenantOption) : [];
      setDeployableTenants(tenants);
      setSelectedTenantId((current) => current || payload.defaultTenantId || tenants[0]?.tenantId || '');
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const loadReviewerAccess = React.useCallback(async (): Promise<void> => {
    if (!canManageClients || !selectedTenantId) {
      setAssignments([]);
      setTenantInstances([]);
      setAssignmentInstanceId('');
      return;
    }
    setLoadingAssignments(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/admin/capa/reviewer-access?${params.toString()}`, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Reviewer access ${response.status}`);
      }
      const payload = (await response.json()) as ReviewerAccessResponse;
      const nextAssignments = Array.isArray(payload.assignments) ? payload.assignments : [];
      const nextInstances = Array.isArray(payload.instances) ? payload.instances : [];
      setAssignments(nextAssignments);
      setTenantInstances(nextInstances);
      setAssignmentInstanceId((current) => {
        if (current && nextInstances.some((entry) => entry.instanceId === current)) {
          return current;
        }
        return nextInstances[0]?.instanceId || '';
      });
    } catch (err) {
      setError((err as Error).message);
      setAssignments([]);
      setTenantInstances([]);
      setAssignmentInstanceId('');
    } finally {
      setLoadingAssignments(false);
    }
  }, [canManageClients, selectedTenantId]);

  const loadOverview = React.useCallback(async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setLoadingOverview(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({
        top: '8',
        tenantId: selectedTenantId
      });
      const response = await fetch(`/api/admin/capa/overview?${params.toString()}`, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Overview ${response.status}`);
      }
      setOverview((await response.json()) as CapaRegisterOverview);
    } catch (err) {
      setError((err as Error).message);
      setOverview(null);
    } finally {
      setLoadingOverview(false);
    }
  }, [selectedTenantId]);

  const loadRows = React.useCallback(async (nextFilters: CapaRegisterFilters): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setLoadingRows(true);
    setError(undefined);
    try {
      const params = new URLSearchParams();
      params.set('tenantId', selectedTenantId);
      if (nextFilters.instanceId) params.set('instanceId', nextFilters.instanceId);
      if (typeof nextFilters.auditId === 'number') params.set('auditId', String(nextFilters.auditId));
      if (nextFilters.status) params.set('status', nextFilters.status);
      if (nextFilters.ownerEmail) params.set('ownerEmail', nextFilters.ownerEmail);
      if (nextFilters.priority) params.set('priority', nextFilters.priority);
      if (nextFilters.overdueOnly) params.set('overdueOnly', 'true');
      if (nextFilters.effectivenessReviewOnly) params.set('effectivenessReviewOnly', 'true');
      if (nextFilters.closedAuditOnly) params.set('closedAuditOnly', 'true');
      params.set('top', String(nextFilters.top || 100));
      const response = await fetch(`/api/admin/capa/register?${params.toString()}`, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Register ${response.status}`);
      }
      const json = (await response.json()) as CapaRegisterListResponse;
      setRows(json);
      setSelectedKey((prev) => {
        const first = json.capas[0];
        if (prev && json.capas.some((row) => `${row.instanceId}:${row.auditId}:${row.capaId}` === prev)) {
          return prev;
        }
        return first ? `${first.instanceId}:${first.auditId}:${first.capaId}` : '';
      });
    } catch (err) {
      setError((err as Error).message);
      setRows(null);
      setSelectedKey('');
    } finally {
      setLoadingRows(false);
    }
  }, [selectedTenantId]);

  const loadDetail = React.useCallback(async (row: CapaRegisterRow | undefined): Promise<void> => {
    if (!row) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(
        `/api/admin/capa/register/${encodeURIComponent(row.instanceId)}/${encodeURIComponent(String(row.auditId))}/${encodeURIComponent(String(row.capaId))}?${params.toString()}`,
        { credentials: 'same-origin' }
      );
      if (!response.ok) {
        throw new Error(`Detail ${response.status}`);
      }
      setDetail((await response.json()) as CapaRegisterDetailResponse);
    } catch (err) {
      setError((err as Error).message);
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedTenantId]);

  const submitReviewerAccess = React.useCallback(async (): Promise<void> => {
    if (!selectedTenantId || !assignmentInstanceId || !reviewerEmail.trim()) {
      setError('Reviewer email and instance are required.');
      return;
    }
    setSavingAssignment(true);
    setError(undefined);
    try {
      const response = await fetch('/api/admin/capa/reviewer-access', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          userId: reviewerEmail.trim(),
          instanceId: assignmentInstanceId
        })
      });
      if (!response.ok) {
        throw new Error(`Grant ${response.status}`);
      }
      setReviewerEmail('');
      await loadReviewerAccess();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingAssignment(false);
    }
  }, [assignmentInstanceId, loadReviewerAccess, reviewerEmail, selectedTenantId]);

  const revokeAssignment = React.useCallback(async (accessId: number): Promise<void> => {
    setSavingAssignment(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/admin/capa/reviewer-access/${encodeURIComponent(String(accessId))}?${params.toString()}`, {
        method: 'DELETE',
        credentials: 'same-origin'
      });
      if (!response.ok) {
        throw new Error(`Revoke ${response.status}`);
      }
      await loadReviewerAccess();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingAssignment(false);
    }
  }, [loadReviewerAccess, selectedTenantId]);

  React.useEffect(() => {
    if (!canViewRegister) {
      return;
    }
    void loadTenants();
  }, [canViewRegister, loadTenants]);

  React.useEffect(() => {
    if (!canManageClients || !selectedTenantId) {
      return;
    }
    void loadReviewerAccess();
  }, [canManageClients, loadReviewerAccess, selectedTenantId]);

  React.useEffect(() => {
    if (!canViewRegister || !selectedTenantId) {
      return;
    }
    void loadOverview();
  }, [canViewRegister, loadOverview, selectedTenantId]);

  React.useEffect(() => {
    if (!canViewRegister || !selectedTenantId) {
      return;
    }
    void loadRows(filters);
  }, [canViewRegister, filters, loadRows, selectedTenantId]);

  React.useEffect(() => {
    const selected = rows?.capas.find((row) => `${row.instanceId}:${row.auditId}:${row.capaId}` === selectedKey);
    void loadDetail(selected);
  }, [loadDetail, rows, selectedKey]);

  const selectedRow = detail?.capa || rows?.capas.find((row) => `${row.instanceId}:${row.auditId}:${row.capaId}` === selectedKey);

  const exportCsv = React.useCallback((): void => {
    if (!rows?.capas?.length) {
      return;
    }
    const headers = [
      'Instance',
      'Audit ID',
      'Audit',
      'Finding',
      'CAPA Reference',
      'Status',
      'Priority',
      'Owner',
      'Target Closure',
      'Overdue',
      'RCA Methodology',
      'RCA Status',
      'Actions Complete',
      'Actions Total',
      'Effectiveness Result',
      'Risk Score',
      'Risk Normalised',
      'Synced At'
    ];
    const lines = rows.capas.map((row) => [
      row.instanceId,
      row.auditId,
      row.auditTitle,
      row.parentFindingTitle,
      row.capaReference,
      row.status,
      row.priority,
      concealNamesInReports && row.ownerEmail ? CONCEALED_REPORT_NAME : row.ownerEmail || '',
      row.targetClosureDate ? formatDate(row.targetClosureDate) : '',
      row.isOverdue ? 'Yes' : 'No',
      row.rcaMethodologyTitle || '',
      row.rcaStatus || '',
      row.linkedActionsCompleted,
      row.linkedActionCount,
      row.effectivenessResult || '',
      row.currentRiskScore ?? '',
      row.currentRiskNormalisedScore ?? '',
      row.syncedAt
    ].map(escapeCsv).join(','));
    const csv = [headers.join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `capa-register-${selectedTenantId || 'tenant'}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [concealNamesInReports, rows, selectedTenantId]);

  const exportPdf = React.useCallback((): void => {
    if (!overview || !rows) {
      return;
    }
    const reportWindow = window.open('', '_blank', 'width=1200,height=900');
    if (!reportWindow) {
      return;
    }
    const comparabilityRows = overview.comparability.instances.map((entry) => `
      <tr>
        <td>${escapeHtml(entry.instanceId)}</td>
        <td>${escapeHtml(entry.siteUrl || '')}</td>
        <td>${entry.capaCount}</td>
        <td>${entry.reportingAudits}</td>
        <td>${escapeHtml(String(entry.avgCapasPerAudit))}</td>
        <td>${escapeHtml(String(entry.completenessRate))}%</td>
        <td>${entry.avgDaysToClose === undefined ? '—' : escapeHtml(String(entry.avgDaysToClose))}</td>
        <td>${entry.overdueCount}</td>
        <td>${entry.effectivenessPendingCount}</td>
        <td>${escapeHtml(entry.dominantMethodologyTitle || '')}</td>
        <td>${escapeHtml(entry.riskFrameworkScales.join(', '))}</td>
      </tr>
    `).join('');
    const registerRows = rows.capas.map((row) => `
      <tr>
        <td>${escapeHtml(row.capaReference)}</td>
        <td>${escapeHtml(row.auditTitle)}</td>
        <td>${escapeHtml(row.parentFindingTitle)}</td>
        <td>${escapeHtml(row.instanceId)}</td>
        <td>${escapeHtml(row.status.replace(/_/g, ' '))}</td>
        <td>${escapeHtml(concealNamesInReports && row.ownerEmail ? CONCEALED_REPORT_NAME : row.ownerEmail || '')}</td>
        <td>${escapeHtml(row.targetClosureDate ? formatDate(row.targetClosureDate) : '')}</td>
        <td>${row.isOverdue ? 'Yes' : 'No'}</td>
        <td>${escapeHtml(row.rcaMethodologyTitle || '')}</td>
        <td>${row.linkedActionsCompleted}/${row.linkedActionCount}</td>
        <td>${escapeHtml(row.effectivenessResult || '')}</td>
      </tr>
    `).join('');
    reportWindow.document.write(`<!doctype html>
      <html>
        <head>
          <title>CAPA Register Summary</title>
          <style>
            body { font-family: Segoe UI, sans-serif; color: #111827; margin: 24px; font-size: 12px; }
            h1, h2 { margin: 0 0 8px; }
            .meta, .totals { margin-bottom: 16px; }
            .totals span { display: inline-block; margin: 0 12px 8px 0; padding: 6px 10px; background: #f3f4f6; border-radius: 999px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #f9fafb; }
          </style>
        </head>
        <body>
          <h1>CAPA Register Summary</h1>
          <div class="meta">
            <div><strong>Produced on:</strong> ${escapeHtml(new Date().toLocaleString())}</div>
            <div><strong>Tenant:</strong> ${escapeHtml(selectedTenant?.displayName || selectedTenant?.sharePointHost || tenant?.tenantName || selectedTenantId)}</div>
            <div><strong>Produced by:</strong> ${escapeHtml(concealNamesInReports && tenant?.userEmail ? CONCEALED_REPORT_NAME : tenant?.userEmail || '')}</div>
          </div>
          <div class="totals">
            <span>Instances: ${overview.totals.reportingInstances}</span>
            <span>Audits: ${overview.totals.reportingAudits}</span>
            <span>CAPAs: ${overview.totals.capaCount}</span>
            <span>Open: ${overview.totals.openCount}</span>
            <span>Overdue: ${overview.totals.overdueCount}</span>
            <span>Effectiveness review: ${overview.totals.effectivenessReviewCount}</span>
          </div>
          <h2>Comparability indicators</h2>
          <table>
            <thead>
              <tr>
                <th>Instance</th>
                <th>Site</th>
                <th>CAPAs</th>
                <th>Audits</th>
                <th>Avg CAPAs / audit</th>
                <th>Completeness</th>
                <th>Avg days to close</th>
                <th>Overdue</th>
                <th>Pending effectiveness</th>
                <th>Dominant RCA</th>
                <th>Risk scales</th>
              </tr>
            </thead>
            <tbody>${comparabilityRows}</tbody>
          </table>
          <h2>Filtered register rows</h2>
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Audit</th>
                <th>Finding</th>
                <th>Instance</th>
                <th>Status</th>
                <th>Owner</th>
                <th>Target</th>
                <th>Overdue</th>
                <th>RCA</th>
                <th>Actions</th>
                <th>Effectiveness</th>
              </tr>
            </thead>
            <tbody>${registerRows}</tbody>
          </table>
        </body>
      </html>`);
    reportWindow.document.close();
    reportWindow.focus();
    window.setTimeout(() => reportWindow.print(), 150);
  }, [concealNamesInReports, overview, rows, selectedTenant, selectedTenantId, tenant?.tenantName, tenant?.userEmail]);

  if (!canViewRegister) {
    return <div className="card">You do not have access to the hosted CAPA register.</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <h1 className="section-title">CAPA Register</h1>
        <p className="muted">
          Hosted cross-instance CAPA visibility for Enterprise operations. CAPA and RCA editing remains in the source SPFx experience.
        </p>
      </div>

      <div style={cardStyle}>
        <AdminTenantSelector
          label="Tenant"
          value={selectedTenantId}
          options={deployableTenants}
          onChange={setSelectedTenantId}
          helperText={
            canManageClients
              ? 'CAPA register is tenant-scoped. Use the selector to switch between covered tenant environments.'
              : `Your QualityReviewer access is limited to the assigned instances for this tenant${typeof selectedTenant?.accessibleInstanceCount === 'number' ? ` (${selectedTenant.accessibleInstanceCount} instance${selectedTenant.accessibleInstanceCount === 1 ? '' : 's'})` : ''}.`
          }
        />
      </div>

      {canManageClients && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <strong>Quality Reviewer access</strong>
            <span className="muted">{loadingAssignments ? 'Loading…' : `${assignments.length} assignment${assignments.length === 1 ? '' : 's'}`}</span>
          </div>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'minmax(220px, 1fr) minmax(240px, 1fr) auto' }}>
            <label>
              <div className="muted">Reviewer email</div>
              <input
                className="input"
                type="email"
                value={reviewerEmail}
                onChange={(event) => setReviewerEmail(event.target.value)}
                placeholder="reviewer@customer.example"
              />
            </label>
            <label>
              <div className="muted">Assigned instance</div>
              <select
                className="input"
                value={assignmentInstanceId}
                onChange={(event) => setAssignmentInstanceId(event.target.value)}
                disabled={tenantInstances.length === 0}
              >
                <option value="">Select instance</option>
                {tenantInstances.map((instance) => (
                  <option key={instance.instanceId} value={instance.instanceId}>
                    {instance.instanceId}
                    {instance.siteUrl ? ` (${instance.siteUrl})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <button
                className="btn secondary"
                type="button"
                onClick={() => void submitReviewerAccess()}
                disabled={savingAssignment || !reviewerEmail.trim() || !assignmentInstanceId}
              >
                Grant access
              </button>
            </div>
          </div>
          <div className="muted" style={{ marginTop: 10 }}>
            QualityReviewer is read-only and instance-scoped. Assignments restrict the hosted CAPA register to the selected installations only.
          </div>
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reviewer</th>
                  <th>Instance</th>
                  <th>Site</th>
                  <th>Granted</th>
                  <th>By</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <tr key={assignment.accessId}>
                    <td>{assignment.userId}</td>
                    <td>{assignment.instanceId}</td>
                    <td>{assignment.siteUrl || '—'}</td>
                    <td>{formatDate(assignment.grantedAt)}</td>
                    <td>{assignment.grantedBy || '—'}</td>
                    <td>
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={() => void revokeAssignment(assignment.accessId)}
                        disabled={savingAssignment}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
                {assignments.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted">No QualityReviewer assignments yet for this tenant.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && <Notice kind="warning">{error}</Notice>}

      <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
        <div style={cardStyle}><strong>CAPAs</strong><div>{loadingOverview ? '…' : overview?.totals.capaCount ?? '—'}</div></div>
        <div style={cardStyle}><strong>Open</strong><div>{loadingOverview ? '…' : overview?.totals.openCount ?? '—'}</div></div>
        <div style={cardStyle}><strong>Overdue</strong><div>{loadingOverview ? '…' : overview?.totals.overdueCount ?? '—'}</div></div>
        <div style={cardStyle}><strong>Effectiveness review</strong><div>{loadingOverview ? '…' : overview?.totals.effectivenessReviewCount ?? '—'}</div></div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <strong>Comparability indicators</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn secondary" type="button" onClick={exportCsv} disabled={!privacyReady || !rows || rows.capas.length === 0}>Export CSV</button>
            <button className="btn secondary" type="button" onClick={exportPdf} disabled={!privacyReady || !rows || rows.capas.length === 0 || !overview}>Export PDF</button>
          </div>
        </div>
        <div style={{ ...gridStyle, gridTemplateColumns: 'minmax(280px, 0.9fr) minmax(0, 1.1fr)' }}>
          <div>
            <div className="muted" style={{ marginBottom: 8 }}>RCA methodology distribution</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {(overview?.comparability.methodologyDistribution || []).map((entry) => (
                <div key={entry.methodologyTitle} style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' }}>
                  <span>{entry.methodologyTitle}</span>
                  <strong>{entry.capaCount}</strong>
                </div>
              ))}
              {!loadingOverview && (!overview || overview.comparability.methodologyDistribution.length === 0) && (
                <div className="muted">No comparability data is available yet for the current tenant scope.</div>
              )}
            </div>
          </div>
          <div>
            <div className="muted" style={{ marginBottom: 8 }}>Instance comparability</div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Instance</th>
                    <th>CAPAs</th>
                    <th>Audits</th>
                    <th>Avg / audit</th>
                    <th>Complete %</th>
                    <th>Avg days close</th>
                    <th>Overdue</th>
                    <th>Pending effectiveness</th>
                    <th>RCA pattern</th>
                    <th>Risk scales</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.comparability.instances || []).map((entry) => (
                    <tr key={entry.instanceId}>
                      <td>{entry.instanceId}</td>
                      <td>{entry.capaCount}</td>
                      <td>{entry.reportingAudits}</td>
                      <td>{entry.avgCapasPerAudit}</td>
                      <td>{entry.completenessRate}%</td>
                      <td>{entry.avgDaysToClose ?? '—'}</td>
                      <td>{entry.overdueCount}</td>
                      <td>{entry.effectivenessPendingCount}</td>
                      <td>{entry.dominantMethodologyTitle || '—'}</td>
                      <td>{entry.riskFrameworkScales.length > 0 ? entry.riskFrameworkScales.join(', ') : '—'}</td>
                    </tr>
                  ))}
                  {!loadingOverview && (!overview || overview.comparability.instances.length === 0) && (
                    <tr>
                      <td colSpan={10} className="muted">No instance comparability rows are available for the current tenant scope.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <label>
            <div className="muted">Instance</div>
            <input className="input" value={filters.instanceId || ''} onChange={(e) => setFilters((prev) => ({ ...prev, instanceId: e.target.value || undefined }))} />
          </label>
          <label>
            <div className="muted">Audit ID</div>
            <input className="input" type="number" value={filters.auditId ?? ''} onChange={(e) => setFilters((prev) => ({ ...prev, auditId: e.target.value ? Number(e.target.value) : undefined }))} />
          </label>
          <label>
            <div className="muted">Owner</div>
            <input className="input" value={filters.ownerEmail || ''} onChange={(e) => setFilters((prev) => ({ ...prev, ownerEmail: e.target.value || undefined }))} />
          </label>
          <label>
            <div className="muted">Status</div>
            <select className="input" value={filters.status || ''} onChange={(e) => setFilters((prev) => ({ ...prev, status: (e.target.value || undefined) as CapaRegisterFilters['status'] }))}>
              <option value="">All</option>
              <option value="raised">Raised</option>
              <option value="rca_in_progress">RCA in progress</option>
              <option value="actions_planned">Actions planned</option>
              <option value="actions_in_progress">Actions in progress</option>
              <option value="actions_complete">Actions complete</option>
              <option value="effectiveness_review">Effectiveness review</option>
              <option value="closed">Closed</option>
              <option value="reopened">Reopened</option>
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          <label><input type="checkbox" checked={Boolean(filters.overdueOnly)} onChange={(e) => setFilters((prev) => ({ ...prev, overdueOnly: e.target.checked }))} /> Overdue only</label>
          <label><input type="checkbox" checked={Boolean(filters.effectivenessReviewOnly)} onChange={(e) => setFilters((prev) => ({ ...prev, effectivenessReviewOnly: e.target.checked }))} /> Effectiveness review only</label>
          <label><input type="checkbox" checked={Boolean(filters.closedAuditOnly)} onChange={(e) => setFilters((prev) => ({ ...prev, closedAuditOnly: e.target.checked }))} /> From closed audits only</label>
          <button className="btn secondary" type="button" onClick={() => setFilters(defaultFilters)}>Clear filters</button>
        </div>
      </div>

      <div style={{ ...gridStyle, gridTemplateColumns: 'minmax(0, 2fr) minmax(320px, 1fr)' }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <strong>Hosted register</strong>
            <span className="muted">{loadingRows ? 'Loading…' : `${rows?.filteredCount ?? 0} visible`}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Audit</th>
                  <th>Finding</th>
                  <th>Status</th>
                  <th>Owner</th>
                  <th>Target</th>
                  <th>RCA</th>
                  <th>Actions</th>
                  <th>Snapshot</th>
                </tr>
              </thead>
              <tbody>
                {(rows?.capas || []).map((row) => {
                  const key = `${row.instanceId}:${row.auditId}:${row.capaId}`;
                  return (
                    <tr
                      key={key}
                      onClick={() => setSelectedKey(key)}
                      style={selectedKey === key ? { background: '#f3f4f6', cursor: 'pointer' } : { cursor: 'pointer' }}
                    >
                      <td>{row.capaReference}</td>
                      <td>{row.auditTitle}</td>
                      <td>{row.parentFindingTitle}</td>
                      <td>{row.status.replace(/_/g, ' ')}</td>
                      <td>{row.ownerEmail || '—'}</td>
                      <td>{row.targetClosureDate ? `${formatDate(row.targetClosureDate)}${row.isOverdue ? ' (Overdue)' : ''}` : '—'}</td>
                      <td>{row.rcaMethodologyTitle || (row.rcaRequired ? 'Required' : 'Optional / none')}</td>
                      <td>{row.linkedActionsCompleted}/{row.linkedActionCount}</td>
                      <td>{formatDate(row.syncedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={cardStyle}>
          <strong>Selected CAPA</strong>
          {loadingDetail && <LoadingIndicator label="Loading detail…" />}
          {!loadingDetail && !selectedRow && <p className="muted">Select a CAPA row to inspect the hosted summary.</p>}
          {!loadingDetail && selectedRow && (
            <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
              <div><strong>Reference:</strong> {selectedRow.capaReference}</div>
              <div><strong>Audit:</strong> {selectedRow.auditTitle}</div>
              <div><strong>Finding:</strong> {selectedRow.parentFindingTitle}</div>
              <div><strong>Status:</strong> {selectedRow.status.replace(/_/g, ' ')}</div>
              <div><strong>Priority:</strong> {selectedRow.priority}</div>
              <div><strong>Owner:</strong> {selectedRow.ownerEmail || '—'}</div>
              <div><strong>Raised:</strong> {formatDate(selectedRow.raisedDate)}</div>
              <div><strong>Target closure:</strong> {formatDate(selectedRow.targetClosureDate)}</div>
              <div><strong>Effectiveness:</strong> {selectedRow.effectivenessResult || 'Pending'}</div>
              <div><strong>RCA:</strong> {selectedRow.rcaMethodologyTitle || '—'} {selectedRow.rcaStatus ? `(${selectedRow.rcaStatus.replace(/_/g, ' ')})` : ''}</div>
              <div><strong>Linked Actions:</strong> {selectedRow.linkedActionsCompleted}/{selectedRow.linkedActionCount} complete</div>
              <div><strong>Current risk:</strong> {formatScore(selectedRow.currentRiskScore)} / {formatScore(selectedRow.currentRiskNormalisedScore)}</div>
              <div><strong>Hosted snapshot last received:</strong> {formatDate(selectedRow.syncedAt)}</div>
              <div className="muted">
                The hosted register is updated when the source installation saves CAPA, RCA, Audit, Finding, or Action changes. Open the source CAPA for the live record and its evidence.
              </div>
              {buildSourceUrl(selectedRow) && (
                <a className="btn secondary" href={buildSourceUrl(selectedRow)} target="_blank" rel="noreferrer">
                  Open this CAPA in the source instance
                </a>
              )}
              <div className="muted">
                Tenant: {selectedTenant?.displayName || selectedTenant?.sharePointHost || tenant?.tenantName || selectedTenantId}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CapaRegister;


